import {
	ButtonTypes, Card, ConfirmModal, type SelectOption, RangeSlider, Select, SettingsLayout, Switch
} from "@clintonelec/react-storybook";
import ActionButtons from "Components/ConfigLayout/ActionButtons";
import { MicOption } from "Data/Objects/Audio";
import { selectAudio, setAudioAction } from "Data/Redux/Slices/Settings/Audio/Audio";
import { useAppDispatch, useAppSelector } from "Data/Redux/Store";
import { produce } from "immer";
import type { AudioSettingsState } from "Interfaces";
import { isEqual, merge } from "lodash";
import { type FormEvent, memo, useState } from "react";

const modalContent = `Audio monitoring and/or recording may be prohibited in certain areas.
Always check any local and state laws before enabling audio on the camera.`;

const micOptions: SelectOption[] = [
	{
		label: "Built-In",
		value: MicOption.BUILT_IN
	},
	{
		label: "Line-In",
		value: MicOption.LINE_IN
	}
];

function AudioSettings() {
	const dispatch = useAppDispatch();
	const audioSettings = useAppSelector(selectAudio);
	const [ audioSettingsDiff, setAudioSettingsDiff ] = useState<Partial<AudioSettingsState>>({});

	const setAudioSettings = (newSettings: AudioSettingsState) => {
		dispatch(setAudioAction(newSettings));
	};

	const localAudioSettings = produce(audioSettings, draft => {
		merge(draft, audioSettingsDiff);
	});

	const { enableAudio, micOption, micVolume, speakerVolume } = localAudioSettings;
	const disabled = isEqual(localAudioSettings, audioSettings);

	const handleReset = (event: FormEvent<HTMLFormElement>) => {
		event.preventDefault();

		setAudioSettingsDiff({});
	};

	const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
		event.preventDefault();

		setAudioSettings(localAudioSettings);
		setAudioSettingsDiff({});
	};

	const handleMicOptionSelect = (value: string | null) => {
		if (!value) {
			return;
		}

		setAudioSettingsDiff(produce(audioSettingsDiff, draft => {
			draft.micOption = value as MicOption;
		}));
	};

	const handleMicVolumeChange = (value: number | number[]) => {
		if (Array.isArray(value)) {
			return;
		}

		setAudioSettingsDiff(produce(audioSettingsDiff, draft => {
			draft.micVolume = value * 5;
		}));
	};

	const handleSpeakerVolumeChange = (value: number | number[]) => {
		if (Array.isArray(value)) {
			return;
		}

		setAudioSettingsDiff(produce(audioSettingsDiff, draft => {
			draft.speakerVolume = value * 5;
		}));
	};

	const handleEnableAudio = (checked: boolean) => {
		setAudioSettingsDiff(produce(audioSettingsDiff, draft => {
			draft.enableAudio = checked;
		}));
	};

	const disableAudio = () => handleEnableAudio(false);

	return (
		<SettingsLayout.Form className="audio-settings-form" onReset={ handleReset } onSubmit={ handleSubmit }>
			<SettingsLayout.ScrollableContainer>
				<Card size="small" title="Audio">
					<div className="form-row">
						<span>Enable Audio</span>
						<ConfirmModal
							cancelButton={ { text: "Cancel", onClick: disableAudio, type: ButtonTypes.DANGER } }
							modalContent={ modalContent }
							okButton={ { text: "Confirm", type: ButtonTypes.TERTIARY } }
							title="Attention"
							visible={ audioSettingsDiff?.enableAudio }
						/>
						<div className="form-switch-wrapper">
							<Switch
								checked={ enableAudio }
								formName="enableAudioSwitch"
								onChange={ handleEnableAudio }
							/>
						</div>
					</div>
					<div className="form-row">
						<span>Mic</span>
						<Select
							allowClear={ false }
							className="form-select"
							disabled={ !enableAudio }
							onSelect={ handleMicOptionSelect }
							options={ micOptions }
							value={ micOption }
						/>
					</div>
					<div className="form-row">
						<span>Mic Volume</span>
						<div className="form-select">
							<RangeSlider
								disabled={ !enableAudio }
								max={ 20 }
								min={ 0 }
								onChange={ handleMicVolumeChange }
								value={ micVolume / 5 }
							/>
						</div>
					</div>
					<div className="form-row">
						<span>Speaker Volume</span>
						<div className="form-select">
							<RangeSlider
								disabled={ !enableAudio }
								max={ 20 }
								min={ 0 }
								onChange={ handleSpeakerVolumeChange }
								value={ speakerVolume / 5 }
							/>
						</div>
					</div>
				</Card>
			</SettingsLayout.ScrollableContainer>
			<ActionButtons disabled={ disabled } />
		</SettingsLayout.Form>
	);
}

export default memo(AudioSettings);
